import React from "react";
import { FileText, Download, ArrowRight, ChartBar, ChartPie, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const reports = [{
  title: "2024 State of Hispanic Business",
  size: "4.2 MB",
  date: "Oct 2024",
  icon: <ChartBar className="w-6 h-6 text-chart-1" />
}, {
  title: "Colorado Economic Impact Report",
  size: "3.8 MB",
  date: "Sep 2024",
  icon: <TrendingUp className="w-6 h-6 text-chart-2" />
}, {
  title: "Workforce & Education Analysis",
  size: "2.5 MB",
  date: "Aug 2024",
  icon: <ChartPie className="w-6 h-6 text-chart-3" />
}];
export default function EconomicIntelligenceHub() {
  const {
    ref: titleRef,
    isVisible: isTitleVisible
  } = useRevealOnScroll();
  const {
    ref: cardsRef,
    isVisible: isCardsVisible
  } = useRevealOnScroll();
  return <section id="economic-intelligence-hub" className="dark bg-background text-foreground relative min-h-[85vh] flex flex-col justify-center items-center py-20 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/data_viz_mesh_bg.png')] bg-cover bg-center opacity-40 mix-blend-overlay" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background" />
        {}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="container relative z-10 px-4 mx-auto flex flex-col items-center">
        {}
        <div ref={titleRef} className={`text-center max-w-4xl mx-auto mb-16 transition-all duration-1000 transform ${isTitleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium tracking-wider uppercase mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            Data & Research Division
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-white/90 to-white/70 drop-shadow-sm">
            Economic Intelligence Hub
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Access real-time insights, market rankings, and comprehensive economic reports defining the $40B+ Hispanic market in Colorado.
          </p>
        </div>

        {}
        <div ref={cardsRef} className={`w-full max-w-6xl transition-all duration-1000 delay-300 transform ${isCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="flex flex-col md:flex-row items-center justify-between mb-8 px-2">
            <h2 className="text-2xl font-serif font-semibold flex items-center gap-3">
              <FileText className="w-6 h-6 text-secondary" />
              Quick Downloads
            </h2>
            <Link to="/IntelligencePage" className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mt-4 md:mt-0">
              View All Reports
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reports.map((report, index) => <Card key={index} data-index={index} className="group relative overflow-hidden border-white/10 bg-white/5 backdrop-blur-md hover:bg-white/10 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/5 hover:-translate-y-1">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-3 rounded-lg bg-background/50 border border-white/5 group-hover:border-primary/20 transition-colors">
                      {report.icon}
                    </div>
                    <span className="text-xs font-mono text-muted-foreground bg-background/30 px-2 py-1 rounded">
                      PDF
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-semibold mb-2 text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {report.title}
                  </h3>
                  
                  <div className="mt-auto pt-4 flex items-center justify-between border-t border-white/5">
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground">Published</span>
                      <span className="text-sm font-medium">{report.date}</span>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-muted-foreground">Size</span>
                      <span className="text-sm font-medium">{report.size}</span>
                    </div>
                  </div>

                  <Button variant="ghost" className="w-full mt-4 bg-white/5 hover:bg-primary hover:text-primary-foreground border border-white/5 group-hover:border-primary/20 transition-all">
                    <Download className="w-4 h-4 mr-2" />
                    Download Report
                  </Button>
                </CardContent>
                
                {}
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/0 via-primary/0 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </Card>)}
          </div>
        </div>
      </div>
    </section>;
}